
import { api } from './base';
import { cachedFetch, invalidateCached } from '@/shared/lib/refCache';

export type QuorumMode = 'all' | 'any' | 'count' | 'percent';
export type RejectPolicy = 'block' | 'continue';

export interface CommitteeMemberInfo {
  userId: string;
  displayName: string;
}

export interface CommitteeLevel {
  id?: string;
  position?: number;
  name: string;
  quorumMode: QuorumMode;
  quorumValue: number | null;
  onReject: RejectPolicy;
  isOptional: boolean;
  slaHours: number | null;
  memberIds: string[];
  members?: CommitteeMemberInfo[];
}

export interface ApprovalCommittee {
  id: string;
  key: string;
  name: string;
  description: string | null;
  isActive: boolean;
  levels: CommitteeLevel[];
  inUse?: boolean;
}

export interface CommitteeBrief {
  id: string;
  key: string;
  name: string;
  description: string | null;
  levels: {
    name: string;
    quorumMode: QuorumMode;
    quorumValue: number | null;
    isOptional: boolean;
    membersCount: number;
  }[];
}

export interface CommitteeRuleCondition {
  field: string;
  op?: 'eq' | 'ne' | 'in' | 'filled' | 'empty' | 'gt' | 'lt';
  value?: string | number | boolean | string[];
}

export interface CommitteeRule {
  id?: string;
  committeeId: string;
  priority?: number;
  conditions: CommitteeRuleCondition[];
  enabled: boolean;
}

export type RuleValueType = 'enum' | 'boolean' | 'number' | 'text';

export interface RuleFieldDescriptor {
  key: string;
  label: string;
  valueType: RuleValueType;
  options?: { value: string; label: string }[];
  group: 'ticket' | 'form';
  hint?: string;
}

export interface CommitteeMeta {
  quorumModes: QuorumMode[];
  rejectPolicies: RejectPolicy[];
  ruleFields: RuleFieldDescriptor[];
}

function invalidate<T>(res: T): T {
  invalidateCached('approval-committees:');
  return res;
}

export const approvalCommitteesApi = {
  list: () => api.get<ApprovalCommittee[]>('/api/v1/admin/approval-committees'),
  meta: () => api.get<CommitteeMeta>('/api/v1/admin/approval-committees/meta'),
  get: (id: string) => api.get<ApprovalCommittee>(`/api/v1/admin/approval-committees/${id}`),
  create: (input: { key: string; name: string; description?: string | null }) =>
    api.post<ApprovalCommittee>('/api/v1/admin/approval-committees', input).then(invalidate),
  update: (id: string, input: { name?: string; description?: string | null; isActive?: boolean }) =>
    api.patch<ApprovalCommittee>(`/api/v1/admin/approval-committees/${id}`, input).then(invalidate),
  remove: (id: string) =>
    api.delete<unknown>(`/api/v1/admin/approval-committees/${id}`).then(invalidate),
  saveLevels: (id: string, levels: CommitteeLevel[]) =>
    api
      .put<ApprovalCommittee>(`/api/v1/admin/approval-committees/${id}/levels`, {
        levels: levels.map(({ id: _id, position: _position, members: _members, ...level }) => level),
      })
      .then(invalidate),

  listRules: () => api.get<CommitteeRule[]>('/api/v1/admin/approval-committee-rules'),
  saveRules: (rules: CommitteeRule[]) =>
    api
      .put<CommitteeRule[]>('/api/v1/admin/approval-committee-rules', {
        rules: rules.map((r) => ({
          committeeId: r.committeeId,
          conditions: r.conditions,
          enabled: r.enabled,
        })),
      })
      .then(invalidate),

  listBrief: () =>
    cachedFetch('approval-committees:brief', 5 * 60_000, () =>
      api.get<CommitteeBrief[]>('/api/v1/approval-committees'),
    ),
};
